import React, { useState, useEffect } from 'react';
import {
  X,
  Package,
  Edit,
  Save,
  AlertTriangle,
  CheckCircle,
  Truck,
  Clock
} from 'lucide-react';
import {
  OptimizedContainer,
  OptimizedOrder
} from '../types/optimization';
import OptimizationService from '../services/optimizationService';

interface OptimizationEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  container?: OptimizedContainer;
  orderToEdit?: { order: OptimizedOrder; containerId: string };
  onSave: (updatedData: any) => void;
}

type EditMode = 'container' | 'order';

const OptimizationEditModal: React.FC<OptimizationEditModalProps> = ({
  isOpen,
  onClose,
  container,
  orderToEdit,
  onSave
}) => {
  const [editMode, setEditMode] = useState<EditMode>('container');
  const [containerData, setContainerData] = useState<Partial<OptimizedContainer>>({});
  const [orderData, setOrderData] = useState<Partial<OptimizedOrder>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (container) {
      setEditMode('container');
      setContainerData({
        id: container.id,
        status: container.status || 'pending'
      });
    }
    
    if (orderToEdit) {
      setEditMode('order');
      setOrderData({
        id: orderToEdit.order.id,
        quantity_to_order: orderToEdit.order.quantity_to_order,
        status: orderToEdit.order.status || 'pending'
      });
    }
    
    setErrors({});
  }, [container, orderToEdit, isOpen]);

  const validateContainerData = () => {
    const newErrors: Record<string, string> = {};
    
    if (!containerData.status) {
      newErrors.status = 'Le statut est requis';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateOrderData = () => {
    const newErrors: Record<string, string> = {};
    
    if (!orderData.quantity_to_order || (orderData.quantity_to_order && orderData.quantity_to_order <= 0)) {
      newErrors.quantity_to_order = 'La quantité doit être supérieure à 0';
    }
    
    if (orderToEdit && orderData.quantity_to_order && orderData.quantity_to_order < orderToEdit.order.moq) {
      newErrors.quantity_to_order = `La quantité doit être supérieure ou égale au MOQ (${orderToEdit.order.moq})`;
    }
    
    if (!orderData.status) {
      newErrors.status = 'Le statut est requis';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateProjectedCoverage = () => {
    if (!orderToEdit || !orderData.quantity_to_order) {
      return orderToEdit?.order.projected_coverage || 0;
    }
    
    // Simple calculation - in a real scenario, this would call the backend
    const ratio = orderData.quantity_to_order / orderToEdit.order.quantity_to_order;
    const baseIncrease = orderToEdit.order.projected_coverage - orderToEdit.order.current_coverage;
    return orderToEdit.order.current_coverage + (baseIncrease * ratio);
  };

  const calculateNewVolume = () => {
    if (!orderToEdit || !orderData.quantity_to_order) {
      return orderToEdit?.order.volume_m3 || 0;
    }
    
    return orderData.quantity_to_order * orderToEdit.order.unit_volume_m3;
  };

  const handleSave = async () => {
    const isValid = editMode === 'container' 
      ? validateContainerData() 
      : validateOrderData();
    
    if (!isValid) return;
    
    setIsSaving(true);
    
    try {
      // In a real implementation, these would call backend APIs
      if (editMode === 'container' && container) {
        await onSave({
          type: 'container',
          containerId: container.id,
          data: containerData
        });
      } else if (editMode === 'order' && orderToEdit) {
        await onSave({
          type: 'order',
          orderId: orderToEdit.order.id,
          containerId: orderToEdit.containerId,
          data: {
            ...orderData,
            projected_coverage: calculateProjectedCoverage(),
            volume_m3: calculateNewVolume()
          }
        });
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving:', error);
      setErrors({ general: 'Erreur lors de la sauvegarde' });
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusOptions = () => [
    { value: 'pending', label: 'En attente', icon: Clock, color: 'text-yellow-600' },
    { value: 'validated', label: 'Validé', icon: CheckCircle, color: 'text-green-600' },
    { value: 'ordered', label: 'Commandé', icon: Truck, color: 'text-blue-600' },
    { value: 'cancelled', label: 'Annulé', icon: AlertTriangle, color: 'text-red-600' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {editMode === 'container' ? (
              <>
                <Package className="text-blue-600" size={24} />
                <h2 className="text-xl font-semibold text-gray-900">
                  Modifier le container
                </h2>
              </>
            ) : (
              <>
                <Edit className="text-green-600" size={24} />
                <h2 className="text-xl font-semibold text-gray-900">
                  Modifier la commande
                </h2>
              </>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Error Message */}
          {errors.general && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center gap-2">
                <AlertTriangle className="text-red-600" size={16} />
                <p className="text-red-700">{errors.general}</p>
              </div>
            </div>
          )}

          {/* Container Edit Mode */}
          {editMode === 'container' && container && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Type de container</p>
                  <p className="font-medium">
                    {OptimizationService.formatContainerType(container.container_type)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Groupe de localisation</p>
                  <p className="font-medium">{container.location_group}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Taux de remplissage</p>
                  <p className="font-medium">
                    {OptimizationService.formatFillRate(container.fill_rate)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Nombre de commandes</p>
                  <p className="font-medium">{container.orders?.length || 0}</p>
                </div>
              </div>

              {/* Status Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Statut du container
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {getStatusOptions().map((status) => {
                    const IconComponent = status.icon;
                    return (
                      <button
                        key={status.value}
                        type="button"
                        onClick={() => setContainerData(prev => ({ ...prev, status: status.value as any }))}
                        className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                          containerData.status === status.value
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <IconComponent className={status.color} size={20} />
                        <span className="font-medium">{status.label}</span>
                      </button>
                    );
                  })}
                </div>
                {errors.status && (
                  <p className="text-red-600 text-sm mt-1">{errors.status}</p>
                )}
              </div>
            </div>
          )}

          {/* Order Edit Mode */}
          {editMode === 'order' && orderToEdit && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Article</p>
                  <p className="font-medium">{orderToEdit.order.article_ref}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Fournisseur</p>
                  <p className="font-medium">{orderToEdit.order.supplier_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">MOQ</p>
                  <p className="font-medium">{orderToEdit.order.moq.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Couverture actuelle</p>
                  <p className="font-medium">
                    {OptimizationService.formatCoverage(orderToEdit.order.current_coverage)}
                  </p>
                </div>
              </div>

              {/* Quantity Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantité à commander
                </label>
                <input
                  type="number"
                  min={orderToEdit.order.moq}
                  step="1"
                  value={orderData.quantity_to_order || ''}
                  onChange={(e) => setOrderData(prev => ({ 
                    ...prev, 
                    quantity_to_order: parseInt(e.target.value) || 0 
                  }))}
                  className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.quantity_to_order ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder={`Minimum: ${orderToEdit.order.moq}`}
                />
                {errors.quantity_to_order && (
                  <p className="text-red-600 text-sm mt-1">{errors.quantity_to_order}</p>
                )}
              </div>

              {/* Projected Impact */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Impact projeté</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-blue-700">Nouvelle couverture</p>
                    <p className="font-medium text-blue-900">
                      {OptimizationService.formatCoverage(calculateProjectedCoverage())}
                    </p>
                  </div>
                  <div>
                    <p className="text-blue-700">Nouveau volume</p>
                    <p className="font-medium text-blue-900">
                      {OptimizationService.formatVolume(calculateNewVolume())}
                    </p>
                  </div>
                </div>
              </div>

              {/* Status Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Statut de la commande
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {getStatusOptions().map((status) => {
                    const IconComponent = status.icon;
                    return (
                      <button
                        key={status.value}
                        type="button"
                        onClick={() => setOrderData(prev => ({ ...prev, status: status.value as any }))}
                        className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                          orderData.status === status.value
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <IconComponent className={status.color} size={20} />
                        <span className="font-medium">{status.label}</span>
                      </button>
                    );
                  })}
                </div>
                {errors.status && (
                  <p className="text-red-600 text-sm mt-1">{errors.status}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Sauvegarde...
              </>
            ) : (
              <>
                <Save size={16} />
                Sauvegarder
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OptimizationEditModal;
